import Lenis from 'lenis';
import { regionalInteractive } from "./march-madness/regional-interactive";
import { recapDonutCharts } from "./march-madness/recap-donuts";
import { viewershipGraphs } from "./march-madness/viewership-graphs";
import { championshipGraphs } from "./march-madness/championship-graphs";

const marchMadnessFunctions = (gsap, mm, ScrollTrigger, DrawSVGPlugin, barba, Flip, data) => {
	
	const $container = $(data.next.container);
	
	// Store cleanup functions
	let cleanupFunctions = [];

	// Debounce function to prevent multiple rapid calls
	function debounce(func, wait) {
		let timeout;
		return function(...args) {
			const context = this;
			clearTimeout(timeout);
			timeout = setTimeout(() => func.apply(context, args), wait);
		};
	}

	// Track last window height to detect URL bar changes
	let lastWindowHeight = window.innerHeight;
	
	// Debounced resize handler
	const handleResize = debounce(() => {
		const currentHeight = window.innerHeight;
		// Only trigger if the height change is significant (not just URL bar)
		if (Math.abs(currentHeight - lastWindowHeight) > 50) {
			lastWindowHeight = currentHeight;
		}
	}, 250);

	// Add resize listener
	window.addEventListener('resize', handleResize);
	cleanupFunctions.push(() => window.removeEventListener('resize', handleResize));
		
	/***************************************************************
		Smooth scrolling
	***************************************************************/
	// Initialize Lenis
	const lenis = new Lenis({
		touchMultiplier: 0.5,
		allowNestedScroll: true,
	});

	// Use requestAnimationFrame to continuously update the scroll
	function raf(time) {
		lenis.raf(time);
		requestAnimationFrame(raf);
	}

	requestAnimationFrame(raf);
	
	/***************************************************************
		Logo animation
	***************************************************************/
	
	const $replayLogo = $container.find('.replay-logo');
	const $eTop = $replayLogo.find('.e_top');
	const $eMiddle = $replayLogo.find('.e_middle');
	const $eBottom = $replayLogo.find('.e_bottom');
	const logoTL = gsap.timeline({
		repeat: -1,
		yoyo: false,
		defaults: {
			ease: "power2.inOut",
			duration: 0.8,
		},
	});
	
	if ($replayLogo.length) {
		
		logoTL.to($eTop, { scaleX: 0.5})
					.to($eMiddle, { scaleX: 1.25 }, '-=0.8')
					.to($eBottom, { scaleX: 0.5 }, '-=0.8')
					.to($eTop, { scaleX: 0.85})
					.to($eMiddle, { scaleX: 0.95 }, '-=0.8')
					.to($eBottom, { scaleX: 1.02 }, '-=0.8')
					.to($eTop, { scaleX: 0.25})
					.to($eMiddle, { scaleX: 0.6 }, '-=0.8')
					.to($eBottom, { scaleX: 0.65 }, '-=0.8')
					.to($eTop, { scaleX: 1})
					.to($eMiddle, { scaleX: 1 }, '-=0.8')
					.to($eBottom, { scaleX: 1 }, '-=0.8');
		
	}
	
	/***************************************************************
		Line animations
	***************************************************************/
	
	const drawSpeed = 2;
	
	const $horizontalLines = $container.find('.draw-horizontal');
	const $verticalLines = $container.find('.draw-vertical');
		
	// Create IntersectionObserver
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const $element = $(entry.target);
				const isHorizontal = $element.hasClass('draw-horizontal');
				const isVertical = $element.hasClass('draw-vertical');
								
				// Set initial state
				gsap.set($element, {
					transformOrigin: isHorizontal ? "left" : "top",
					transform: isHorizontal ? "scaleX(0)" : "scaleY(0)",
					opacity: 1,
				});
				
				// Animate to final state
				gsap.to($element, {
					transformOrigin: isHorizontal ? "left" : "top",
					transform: isHorizontal ? "scaleX(1)" : "scaleY(1)",
					duration: drawSpeed,
					onComplete: () => {
						observer.unobserve(entry.target);
					}
				});
			}
		});
	}, {
		threshold: 0.2,
		rootMargin: "0px"
	});
	
	// Observe all lines
	if ($horizontalLines.length) {
		$horizontalLines.each(function() {
			observer.observe(this);
		});
	}
	
	if ($verticalLines.length) {
		$verticalLines.each(function() {
			observer.observe(this);
		});
	}
	
	// Clean up observer on page leave
	barba.hooks.beforeLeave(() => {
		observer.disconnect();
	});
	
	/***************************************************************
		Basketball animation
	***************************************************************/
	
	const $regionalIntro = $container.find('.regional-intro');
	const $regionalIntroBasketball = $container.find('.basketball-illo');
	
	// Store the animation in a variable to prevent duplicates
	let basketballAnimation;
	
	if ($regionalIntroBasketball.length && !basketballAnimation) {
		basketballAnimation = gsap.fromTo($regionalIntroBasketball, {
			rotation: 0,
			y: 100,
		}, {
			rotation: 360,
			y: -20,
			ease: "none",
			scrollTrigger: {
				trigger: $regionalIntro,
				start: "top 50%",
				end: "bottom 0%",
				scrub: true,
			}
		});
	}
	
	/***************************************************************
		Totals counter
	***************************************************************/
	
	const $statTexts = $container.find('.recap-totals');
	
	if ($statTexts) {
		
		const $texts = $statTexts.find('.total-text');
		const defaultChars = '0123456789!@#$%+&*';

		$texts.each(function() {

			const $this = $(this);
			const $text = $this.text();

			gsap.to($this, {
				scrambleText: {
					text: $text,
					chars: defaultChars,
					revealDelay:1,
					tweenLength:false,
				},
				onComplete: function() {
					gsap.to($this, {
						scrollTrigger: {
							trigger: $statTexts,
							start: "top 80%",
						},
						scrambleText:{
							text: $text, 
							chars: defaultChars, 
							speed: 2
						},
						onStart: function() {
							$this.addClass('tweening');
						},
						onComplete: function() {
							$this.removeClass('tweening');
						}
					});
				}
			})

		});

	}

	/***************************************************************
		Regional interactive
	***************************************************************/
	
	regionalInteractive(gsap, lenis, Flip, $container);
	
	/***************************************************************
		Charts
	***************************************************************/
	const viewershipCleanup = viewershipGraphs(gsap, $container);
	if (viewershipCleanup) cleanupFunctions.push(viewershipCleanup);
	
	recapDonutCharts(gsap, $container);
	championshipGraphs(gsap, $container);
	
	/***************************************************************
		Destroy on leave
	***************************************************************/
	
	barba.hooks.beforeLeave((data) => {
		lenis.destroy();
	});
	
}

export { marchMadnessFunctions };